/**
 * The browser's side of notifications: the calls to the `notifications`
 * Edge Function, the service-worker push subscription, and the timezone
 * write. Every request carries the seeker's session token; the function
 * verifies it with GoTrue.
 *
 * Nothing here talks to a push service or an email provider, and nothing
 * here reads the notifications schema: the function does both.
 */
import {
  isWebPushSupported,
  subscribeWebPush,
  unsubscribeWebPush,
} from '@nixxtech/notifications/client'
import { supabase, db } from '../supabase'

export interface PreferenceRow {
  category: string
  enabled: boolean
  transactional: boolean
}

export interface NotificationState {
  vapidPublicKey: string | null
  endpoints: string[]
  preferences: PreferenceRow[]
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notifications`

async function authHeaders(): Promise<Record<string, string>> {
  if (!supabase) throw new Error('Supabase is not configured')
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Not signed in')
  return {
    Authorization: `Bearer ${token}`,
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    'Content-Type': 'application/json',
  }
}

/** A non-2xx answer from the function, carrying its status so callers can act on a specific one. */
export class NotificationsHttpError extends Error {
  readonly status: number
  constructor(status: number) {
    super(`notifications ${status}`)
    this.status = status
  }
}

async function call<T>(init: { method: 'GET' } | { method: 'POST'; body: Record<string, unknown> }): Promise<T> {
  const res = await fetch(FUNCTION_URL, {
    method: init.method,
    headers: await authHeaders(),
    ...(init.method === 'POST' ? { body: JSON.stringify(init.body) } : {}),
  })
  if (!res.ok) throw new NotificationsHttpError(res.status)
  return (await res.json()) as T
}

export function fetchNotificationState(): Promise<NotificationState> {
  return call<NotificationState>({ method: 'GET' })
}

export async function setPreference(category: string, enabled: boolean): Promise<PreferenceRow[]> {
  const r = await call<{ preferences: PreferenceRow[] }>({ method: 'POST', body: { action: 'preference', category, enabled } })
  return r.preferences
}

/**
 * Tell the server a session was completed so it can schedule the free-tier
 * "next session is open" reminder. Fire-and-forget from Close.tsx: a
 * failure here must never touch the session save.
 */
export async function reportSessionCompleted(sessionId: string): Promise<void> {
  try {
    await call({ method: 'POST', body: { action: 'session_completed', sessionId } })
  } catch (err) {
    console.warn('[notifications] session_completed not recorded:', err instanceof Error ? err.message : err)
  }
}

export { isWebPushSupported }

/** Whether the service worker is registered and this browser can subscribe. */
export function pushAvailable(vapidPublicKey: string | null): boolean {
  return !!vapidPublicKey && isWebPushSupported()
}

/**
 * Ask for permission, subscribe this browser, and register the device server-side.
 *
 * A browser keeps one push subscription however many people sign in on it.
 * If someone else's account already registered it, the server answers 409
 * (#41: a device can no longer be taken over). The subscription is then
 * replaced with a fresh one, which gives this browser a new endpoint for the
 * person signed in now. The old endpoint stops working at the push service,
 * so the previous account stops getting alerts on a browser it no longer
 * uses; the package disables that row the first time a push to it bounces.
 */
export async function enablePushOnThisDevice(vapidPublicKey: string): Promise<'on' | 'denied' | 'off'> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'
  const registration = await navigator.serviceWorker.ready
  const subscription = await subscribeWebPush(registration, vapidPublicKey)
  try {
    await call({ method: 'POST', body: { action: 'register', subscription } })
  } catch (err) {
    if (!(err instanceof NotificationsHttpError) || err.status !== 409) throw err
    await unsubscribeWebPush(registration)
    const fresh = await subscribeWebPush(registration, vapidPublicKey)
    await call({ method: 'POST', body: { action: 'register', subscription: fresh } })
  }
  return 'on'
}

/** Unsubscribe this browser and forget the device server-side. */
export async function disablePushOnThisDevice(): Promise<void> {
  const registration = await navigator.serviceWorker.ready
  const endpoint = await unsubscribeWebPush(registration)
  if (endpoint) await call({ method: 'POST', body: { action: 'unregister', endpoint } })
}

/** The endpoint this browser is currently subscribed with, if any. */
export async function currentEndpoint(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null
  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  return existing?.endpoint ?? null
}

/**
 * Keep profiles.timezone current. Called once after auth resolves; writes
 * only when the browser's zone differs from what is stored, so most loads
 * cost one read and no write.
 */
export async function syncTimezone(userId: string): Promise<void> {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!zone) return
    const { data } = await db().from('profiles').select('timezone').eq('id', userId).maybeSingle()
    const stored = (data as { timezone?: string | null } | null)?.timezone ?? null
    if (stored === zone) return
    await db().from('profiles').update({ timezone: zone }).eq('id', userId)
  } catch (err) {
    console.warn('[notifications] timezone not synced:', err instanceof Error ? err.message : err)
  }
}
